import { useState } from 'react';
import { Box, CircularProgress, TablePagination, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useSavedQueries } from '../../../../hooks/QueryBuilder/useSavedQueries';
import { useQueryResult, getQueryResultStatus, getQueryResultTotal } from '../../../../hooks/QueryBuilder/useQueryResult';
import { useQueryCount } from '../../../../hooks/QueryBuilder/useQueryCount';
import { FilterHeader } from '../../../Clients/QueryBuilder/components/FilterHeader';
import { AvailableGroup } from '../../../../context/QueryBuilderDataContext';
import { QueryField } from '../../../../types/QueryBuilder';
import QueryPreviewDialog, { QueryPreviewTarget } from './QueryPreviewDialog';
import SavedQueriesTable from './targeting/SavedQueriesTable';
import { formatRecipients, settleToast } from './targeting/toasts';

interface ExistingQueriesPickerProps {
  selectedQueryId: number | null;
  availableGroups: AvailableGroup[];
  loadingGroups: boolean;
  fields: QueryField[];
  categories: string[];
  onRefetchGroups: () => void;
  onSelect: (query: { queryId: number; description: string; nbRecipients: number | null }) => void;
}

function ExistingQueriesPicker({ selectedQueryId, availableGroups, loadingGroups, fields, categories, onRefetchGroups, onSelect }: ExistingQueriesPickerProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [isFromCompany, setIsFromCompany] = useState<0 | 1 | undefined>(undefined);
  const [source, setSource] = useState<'SIMPLE' | 'ADVANCED' | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const [submittedSearchTerm, setSubmittedSearchTerm] = useState('');
  const [selectingId, setSelectingId] = useState<string | null>(null);
  const [previewQuery, setPreviewQuery] = useState<QueryPreviewTarget | null>(null);

  const { queries, totalItems, loading } = useSavedQueries({
    page: page + 1,
    itemsPerPage: rowsPerPage,
    groupQueryIds: selectedGroupIds.length > 0 ? selectedGroupIds.join(',') : undefined,
    isFromCompany,
    search: submittedSearchTerm,
    source,
  });

  const { fetchQueryResult } = useQueryResult();
  const { launchQueryCount } = useQueryCount();

  const withFirstPage = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(0);
  };

  const handleReset = () => {
    setSelectedGroupIds([]);
    setIsFromCompany(undefined);
    setSource(undefined);
    setSearchTerm('');
    setSubmittedSearchTerm('');
    setPage(0);
  };

  const handleSelectQuery = async ({ queryId, description }: QueryPreviewTarget) => {
    setSelectingId(queryId);
    const toastId = toast.loading('Récupération du nombre de destinataires...');
    try {
      const res = await fetchQueryResult(queryId);
      const status = getQueryResultStatus(res);
      let count = getQueryResultTotal(res);

      if (!res?.status || status === 'expired' || status === 'error' || count === null) {
        const relaunch = await launchQueryCount(queryId);
        if (relaunch?.count === undefined) {
          settleToast(toastId, 'info', 'Requête sélectionnée — comptage en cours, revenez plus tard pour voir le nombre de destinataires.', 6000);
          onSelect({ queryId: Number(queryId), description, nbRecipients: null });
          return;
        }
        count = relaunch.count;
      }

      settleToast(toastId, 'success', `Requête sélectionnée : ${formatRecipients(count)}`, 4000);
      onSelect({ queryId: Number(queryId), description, nbRecipients: count });
    } catch {
      settleToast(toastId, 'error', 'Erreur lors de la récupération du résultat');
    } finally {
      setSelectingId(null);
    }
  };

  return (
    <Box>
      <FilterHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onSearchSubmit={value => withFirstPage(setSubmittedSearchTerm)(value.trim())}
        selectedGroupIds={selectedGroupIds}
        onSelectedGroupIdsChange={withFirstPage(setSelectedGroupIds)}
        availableGroups={availableGroups}
        loadingGroups={loadingGroups}
        isFromCompany={isFromCompany}
        onTypeChange={withFirstPage(setIsFromCompany)}
        source={source}
        onSourceChange={withFirstPage(setSource)}
        onReset={handleReset}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : queries.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body2" color="text.secondary">Aucune requête trouvée</Typography>
        </Box>
      ) : (
        <>
          <SavedQueriesTable queries={queries} selectedQueryId={selectedQueryId} selectingId={selectingId} onPick={setPreviewQuery} />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={e => withFirstPage(setRowsPerPage)(parseInt(e.target.value, 10))}
            labelRowsPerPage="Lignes par page"
            sx={{ mt: 1 }}
          />
        </>
      )}

      <QueryPreviewDialog
        open={previewQuery !== null}
        query={previewQuery}
        fields={fields}
        categories={categories}
        onClose={() => setPreviewQuery(null)}
        onConfirm={() => {
          if (!previewQuery) return;
          handleSelectQuery(previewQuery);
          setPreviewQuery(null);
        }}
      />
    </Box>
  );
}

export default ExistingQueriesPicker;
